import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import currentRouteId from 'root/src/client/logic/route/selectors/currentRouteId';
import isBrandUserSelector from 'root/src/client/logic/static/selectors/isBrandUserSelector';
import modalModuleIdSelector from 'root/src/client/logic/modal/selectors/modalModuleIdSelector';
import modalPropsSelector from 'root/src/client/logic/modal/selectors/modalPropsSelector';
import displayModal from 'root/src/client/logic/modal/thunks/displayModal';
import { PUBLISHER_SETTINGS_MODAL_MODULE_ID } from 'root/src/shared/descriptions/modules/moduleIds';
import { hasBrandSettings } from 'root/src/shared/util/brandUtils';
import { useAppDispatch, useAppSelector } from 'root/src/client/logic/helpers/useStore';
import { useAccountData } from 'root/src/client/v2/userData/hooks/useAccountData';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { useIsRedesignCohort } from 'root/src/client/v2/layout/hooks/useIsRedesignCohort';
import { RedesignPublisherSettingsModal } from 'root/src/client/v2/profile/features/PublisherSettingsModal/RedesignPublisherSettingsModal';
// Shows the redesign after-login publisher settings modal for the redesign cohort, replacing the legacy
// Redux modal (src/client/web/modal/PublisherSettingsModal.js) that app-init `initRequests` opens for
// brand users with no publisher settings. Non-cohort users keep the legacy modal (rendered by
// `RenderModules`) untouched, so this is a true A/B swap that never edits the legacy component.
//
// It is driven by the persistent store condition (brand user with no `brandName`/`brandWebsite`), not by
// the transient Redux modal state, for two reasons: the redesign modal lives in a context stack that
// `RedesignModalProvider` clears on `routeChangeStart`, and `initRequests` runs fire-and-forget while a
// post-login A/B redirect may navigate. Driving off the store (and re-showing after each navigation)
// keeps the mandatory modal from being stranded. When the brand saves, the store's `accountData` gains
// the settings, the condition flips false, and the modal is no longer re-shown.
export const RedesignPublisherSettingsModalBridge = () => {
    const dispatch = useAppDispatch();
    const router = useRouter();
    const isCohort = useIsRedesignCohort();
    const isBrandUser = useAppSelector(isBrandUserSelector);
    const accountData = useAccountData();
    const { show } = useRedesignModal();
    const routeId = useAppSelector(currentRouteId);
    const legacyModalId = useAppSelector(state => modalModuleIdSelector(state, { routeId }));
    const legacyModalProps = useAppSelector(state => modalPropsSelector(state, { routeId }));
    // The Redux modal's `onSuccess` resolves the (fire-and-forget) app-init await; captured so the
    // redesign save can still settle it. Kept in a ref so re-shows across navigations reuse it.
    const onSuccessRef = useRef(undefined);
    const needsSettings = isCohort && isBrandUser && !!accountData && !hasBrandSettings(accountData);
    // Suppress the legacy Redux modal for the cohort (so `RenderModules` does not render it too) and
    // capture its `onSuccess`.
    useEffect(() => {
        if (isCohort && legacyModalId === PUBLISHER_SETTINGS_MODAL_MODULE_ID) {
            onSuccessRef.current = legacyModalProps === null || legacyModalProps === void 0 ? void 0 : legacyModalProps.onSuccess;
            dispatch(displayModal(undefined));
        }
    }, [isCohort, legacyModalId, legacyModalProps, dispatch]);
    // Show while settings are missing, and re-show after a navigation clears the modal stack. The modal
    // closes itself on save; the condition then flips false so it is not shown again.
    useEffect(() => {
        if (!needsSettings) {
            return undefined;
        }
        const showModal = () => show(<RedesignPublisherSettingsModal onSaved={() => { var _a; return (_a = onSuccessRef.current) === null || _a === void 0 ? void 0 : _a.call(onSuccessRef); }}/>, { closeable: false });
        showModal();
        router.events.on('routeChangeComplete', showModal);
        return () => router.events.off('routeChangeComplete', showModal);
    }, [needsSettings, show, router.events]);
    return null;
};
